// src/components/common/filterStyles.ts
//
// Single source of truth for the Soft minimal filter look, shared by the
// React-Admin filter system (CustomFilters + CollapsibleFilterPanel) AND by the
// legacy list pages that build filters by hand with raw MUI fields. Importing the
// same helpers keeps every page's filters visually identical.
import type { Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

/** Styling for a single filter field (36px, white surface, soft radius). */
export const filterFieldSx = (theme: Theme): SystemStyleObject<Theme> => ({
  width: { xs: '100%', sm: 'auto' },

  '& .MuiInputBase-root': {
    height: 36,
    minHeight: '36px !important',
    fontSize: '0.8125rem',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.custom?.radius?.small ?? '8px',
  },

  '& .MuiInputLabel-root': {
    fontSize: '0.8125rem',
    '&:not(.MuiInputLabel-shrink)': {
      transform: 'translate(14px, 8px) scale(1)',
    },
  },

  '& .MuiSelect-icon': {
    top: 'calc(50% - 12px)',
    right: '6px',
  },

  '& .MuiInputAdornment-positionEnd': {
    marginLeft: 0,
    '& .MuiIconButton-root': { padding: '4px' },
  },
});

/**
 * Styling for the filter panel container: a floating card that also sizes any
 * field placed inside it (so raw MUI <TextField>s on legacy pages match the
 * React-Admin inputs without extra per-field styling).
 */
export const filterPanelSx = (theme: Theme): SystemStyleObject<Theme> => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: { xs: 1, sm: 1.25 },
  alignItems: 'center',
  p: { xs: 1.5, sm: 2 },
  mt: 1,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.custom?.radius?.card ?? '10px',
  boxShadow: 'none',

  // Size any field inside consistently (RA inputs + raw MUI TextFields).
  '& .MuiInputBase-root': {
    minHeight: '36px !important',
    height: 36,
    fontSize: '0.8125rem',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.custom?.radius?.small ?? '8px',
  },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
  '& .MuiFormControl-root': { m: 0 },
  '& .MuiFormHelperText-root': { display: 'none' },
  // Neutralise child margins so spacing is owned by the panel gap.
  '& > *': { mr: '0 !important', mb: '0 !important' },
});
